import React, { useCallback, useContext, useEffect, useReducer, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  TextField,
} from "@mui/material";
import withStyles from '@mui/styles/withStyles';
import makeStyles from '@mui/styles/makeStyles';
import SvgIcon from '@mui/material/SvgIcon'
import { TreeItem } from '@mui/x-tree-view';
import { LoadingCircFolderIcon } from './Icons'
import { FoldersGetContents, FoldersEditAutoRedact } from 'modules/Rest'
import { padNumbers, useIsMounted, setModes } from 'modules/Utils'
import { MessageContext } from './Contexts'
import ActionButtons from './ActionButtons'

const useStyles = makeStyles({
  noSetValue: {
    opacity: 0.5,
  },
  loading: {
    opacity: 0.5,
    marginLeft: '22px'
  },
  setValue: {
    opacity: 1.0,
  },
  formControl: {
    minWidth: 350,
  }
})

const StyledSvgIcon = withStyles({
  root: {
    fontSize: '18px',
    marginBottom: '-3px',
    marginRight: '6px',
    marginLeft: '-2px',
  },
})(SvgIcon)

// Folder icon reflecting the effective auto-redact value: marked folders get
// an "R" badge (same visual language as the W/S rights badges)
const createAutoRedactIcon = function(effective) {
  return (
    <StyledSvgIcon>
      <path
        d='M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z'
        fill={effective ? '#f2dea5' : '#aaa'}
      />
      {effective && (
        <text x='18' y='22' fontSize='12' fontWeight='bold' textAnchor='middle'>
          R
        </text>
      )}
    </StyledSvgIcon>
  )
}

const autoRedactSelection = [
  { label: 'Default', value: 'null' },
  { label: 'Automatic redaction on', value: 'true' },
  { label: 'Automatic redaction off', value: 'false' },
]

const setValueReducer = (oldValue, newValue) => {
  const { strValue, rawValue } = { strValue: '', rawValue: null, ...newValue }
  if (strValue) {
    return strValue
  } else if (rawValue != null) {
    return JSON.stringify(rawValue)
  } else {
    return JSON.stringify(null)
  }
}

function LoadingIcon() {
  return <LoadingCircFolderIcon size={14} thickness={5}/>
}

export default function AutoRedactTreeItem({ nodeId, folder, isPlaceholder, autoload, toggledNodes, onReload: onSuperReload }) {
  const [subfolders, setSubfolders] = useState([{ folder_name: 'Loading...', folder: 'placeholder-' + nodeId, isPlaceholder: true }])
  const [fetched, setFetched] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [cacheSubfolders, setCacheSubfolders] = useState(false)
  const [valueStr, setValue] = useReducer(setValueReducer, { rawValue: null })
  const { messageDispatch } = useContext(MessageContext)
  const isMounted = useIsMounted()

  const [modeDialog, setModeDialog] = useState('')
  // Bumped on save to remount the descendant items: their cached subtrees
  // would otherwise keep showing the previous effective values until a page
  // reload. itemIds are unchanged, so expansion state survives and expanded
  // descendants re-fetch via the toggledNodes/autoload effect.
  const [subtreeGeneration, setSubtreeGeneration] = useState(0)

  const fetchFolders = useCallback(async () => {
    if(isMounted.current) {
      setFetching(true)
      setFetched(true)
    }
    try {
      const { data: { folders, success, info } } = await FoldersGetContents({
        folder: nodeId,
        incl_auto_redact: true,
      })
      if (success && isMounted.current) {
        const sortedFolders = [...folders]
        sortedFolders.sort((folderA, folderB) => {
          return padNumbers(folderA.folder_name) > padNumbers(folderB.folder_name) ? 1 : -1
        })
        setSubfolders(sortedFolders)
        setFetching(false)
      } else if (!success && isMounted.current) {
        throw new Error(info ? info : "An error occurred")
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }, [nodeId, isMounted, messageDispatch])

  useEffect(() => {
    // Load subfolders if the folder has been toggled or if autoload has been set to true for the folder
    if (!fetched && ((toggledNodes && toggledNodes.includes(nodeId)) || autoload)) {
      fetchFolders()
    }
  }, [fetched, nodeId, toggledNodes, autoload, fetchFolders])

  useEffect(() => {
    // Pre-load subfolders if the folder has been toggled
    if (toggledNodes && toggledNodes.includes(nodeId)) {
      setCacheSubfolders(true)
    }
  }, [toggledNodes, nodeId])

  useEffect(() => {
    setValue({ rawValue: folder.s_auto_redact })
  }, [folder])

  const onLabelClick = () => {
    setModeDialog('editing')
  }

  const onCancel = () => {
    setValue({ rawValue: folder.s_auto_redact })
    setModeDialog('')
  }

  const onSave = async () => {
    try {
      setModeDialog('pending')
      const { data: { success, info } } = await FoldersEditAutoRedact({
        folder: nodeId,
        auto_redact: JSON.parse(valueStr),
      })
      if (success) {
        await setModes(setModeDialog, ['completing', 1500, ''])
        onSuperReload && onSuperReload() // Trigger superfolder reload
        fetchFolders() // Trigger reload of this folder
        setSubtreeGeneration(generation => generation + 1) // Refresh the whole subtree
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      setModeDialog('')
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  const onValueChange = event => {
    setValue({ strValue: event.target.value })
  }

  const styles = useStyles()

  if (isPlaceholder) {
    return (
      <TreeItem
        itemId={nodeId}
        label={<span className={styles.loading}>{folder.folder_name}</span>}
        slots={{ endIcon: LoadingIcon }}
      />
    )
  } else {
    return <>
      <Dialog open={modeDialog !== ''}>
        <DialogTitle>Edit automatic redaction</DialogTitle>
        <DialogContent>
          <FormControl className={styles.formControl}>
            <TextField select variant="standard" label="Automatic redaction" value={valueStr} onChange={onValueChange}>
              {autoRedactSelection.map(choice => (
                  <MenuItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <ActionButtons
            mode={modeDialog}
            setMode={setModeDialog}
            action={onSave}
            cancel={onCancel}
            actionTitle='Save'
            neverShowEdit={true}
          />
        </DialogActions>
      </Dialog>

      <TreeItem
          itemId={nodeId}
          slots={{ expandIcon: fetching ? LoadingIcon: null }}
          label={
            <div onClick={onLabelClick} className={folder.s_auto_redact != null ? styles.setValue : styles.noSetValue}>
              {createAutoRedactIcon(folder.e_auto_redact)}
              {folder.folder_name}
            </div>
          }
      >
        {subfolders.map(subfolder => (
          <AutoRedactTreeItem
            key={`${subfolder.folder}-${subtreeGeneration}`}
            nodeId={subfolder.folder}
            folder={subfolder}
            isPlaceholder={subfolder.isPlaceholder}
            autoload={cacheSubfolders && !subfolder.isPlaceholder}
            toggledNodes={toggledNodes}
            onReload={fetchFolders}
          />
        ))}
      </TreeItem>
    </>
  }
}
